import { writable, type Readable } from 'svelte/store';
import type { ClientMessage, DisplayView } from '@banger/shared';
import type { BangerPlayer } from '../spotify/player';

/** What the host must click: unlock audio, or reconnect a lost player. */
export type Attention = 'activate' | 'reconnect' | null;

export interface PlaybackController {
  /** Feed every display view (they repeat every 5 s; only changes act). */
  apply(v: DisplayView): void;
  /** `gesture: true` only when called inside a click; otherwise the first confirmed play decides. */
  activate(opts?: { gesture?: boolean }): Promise<void>;
  /** The player is gone (e.g. an SDK error after ready): stop and ask the host to reconnect. */
  markLost(): void;
  attention: Readable<Attention>;
}

/** After this many song_unplayable reports in a row, assume the player, not the songs, is broken. */
export const MAX_CONSECUTIVE_UNPLAYABLE = 3;

type NowPlaying = NonNullable<DisplayView['nowPlaying']>;
// A skipped/unplayable song keeps its round number, so the song id is part of the key.
const keyOf = (np: NowPlaying | null | undefined) => (np ? `${np.round}:${np.service}:${np.trackId}` : null);

export function createPlaybackController(deps: {
  player: BangerPlayer;
  send: (msg: ClientMessage) => void;
}): PlaybackController {
  const { player, send } = deps;
  /** Only tracks for this player's service; another service's id is never sent to it. */
  const mine = (np: NowPlaying | null) => (np && np.service === player.service ? np : null);
  const attention = writable<Attention>(null);
  let latest: DisplayView | null = null;
  let activated = false; // a play has been confirmed or activation came from a click
  let tentative = false; // activation ran outside a click; unproven
  let lost = false;
  let attemptedKey: string | null = null;
  let loadedKey: string | null = null;
  let playing = false;
  let unplayableStreak = 0;
  let queue = Promise.resolve();

  function goLost(): void {
    lost = true;
    attention.set('reconnect');
  }

  // Runs serially, always against the newest view.
  async function reconcile(): Promise<void> {
    const v = latest;
    if (!v) return;
    const np = mine(v.nowPlaying);
    const key = keyOf(np);

    if (!lost && np && key !== attemptedKey && !v.paused) {
      if (!activated && !tentative) { attention.set('activate'); return; }
      attemptedKey = key;
      const r = await player.play(np.trackId, np.startMs);
      if (r === 'ok') {
        activated = true;
        tentative = false;
        unplayableStreak = 0;
        loadedKey = key;
        playing = true;
        attention.set(null);
      } else {
        loadedKey = null;
        playing = false;
        if (!activated) {
          // Unproven activation: likely blocked audio, not a bad song. Retry after a click.
          tentative = false;
          attemptedKey = null;
          attention.set('activate');
          return;
        }
        if (r === 'device_error') return goLost();
        if (keyOf(latest?.nowPlaying) !== key) return; // superseded meanwhile
        if (unplayableStreak >= MAX_CONSECUTIVE_UNPLAYABLE) return goLost();
        unplayableStreak += 1;
        send({ type: 'song_unplayable', round: np.round });
        return;
      }
    }

    const now = latest!;
    const want = !lost && mine(now.nowPlaying) !== null && !now.paused && loadedKey === keyOf(mine(now.nowPlaying));
    if (want !== playing) {
      playing = want;
      await (want ? player.resume() : player.pause());
    }
  }

  function schedule(): void {
    queue = queue.then(reconcile).catch(() => { /* next view retries */ });
  }

  return {
    attention,
    apply(v) {
      latest = v;
      schedule();
    },
    async activate({ gesture = false } = {}) {
      let ok = true;
      try { await player.activate(); } catch { ok = false; }
      if (ok && gesture) activated = true;
      else tentative = true;
      attention.set(null);
      schedule();
    },
    markLost() {
      goLost();
      schedule();
    },
  };
}

// The display's live controller, so the lobby's Start click can unlock audio.
let current: PlaybackController | null = null;
let gestureSeen = false; // Start was clicked before the player finished loading

export function setPlaybackController(c: PlaybackController | null): void {
  current = c;
  if (c && gestureSeen) void c.activate(); // outside the click: unproven until a play confirms
}

/** Call from a click handler. */
export async function activatePlayback(): Promise<void> {
  gestureSeen = true;
  await current?.activate({ gesture: true });
}
